import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Chip,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
  styled,
  useMediaQuery,
} from "@mui/material";
import { ArrowBackIosRounded, AttachFileRounded } from "@mui/icons-material";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ColumnContainer from "../../../common/ColumnContainer.js";
import { SubtitleContainer, TitleContainer } from "../../style.js";
import RowContainer from "../../../common/RowContainer.js";
import { adminDashboardRoute } from "../../../../utils/routes/Routes.js";
import {
  useAddMessageToTicketMutation,
  useGetTicketByTicketIdQuery,
  useUpdateSingleTicketMutation,
} from "../../../../redux/features/tickets/ticketsApi.js";
import {
  getCurrentTicketId,
  selectSingleTicket,
} from "../../../../redux/features/tickets/ticketsSlice.js";
import { ChatComponent } from "../../../common/ChatComponent.js";
import "./TicketsStyle.less";
import { DashboardButton } from "../../../common/DashboardButton.js";
import SelectComponent from "../../../common/SelectComponent.js";
// import DownloadFile from "../../../../utils/helpers/downloadFile.js";
import { DatePicker, DesktopDatePicker } from "@mui/x-date-pickers";
import { selectAuthData } from "../../../../redux/features/auth/authSlice.js";
import { handleDownloadClick } from "../../../../utils/helpers/downloadFile.js";
import { FormattedMessage, useIntl } from "react-intl";
import { SkeletonLoading } from "../../../common/SkeletonLoading.js";
import { ErrorLoading } from "../../../common/ErrorLoading.js";
import { translateFilterKeys } from "../../../../utils/helpers/translateFilterKeys.js";
import { selectLanguage } from "../../../../redux/features/language/languageSlice.js";
import { FormatDateTime } from "../../../../utils/helpers/FormatDateTime.js";

export const SingleTicketPage = () => {
  // INIT
  const { id: ticketId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInput = useRef(null);
  const isDesktop = useMediaQuery("(min-width:960px)");
  const intl = useIntl();

  // REDUX
  const {
    data,
    isLoading,
    isError,
    refetch: refetchSingleTicket,
  } = useGetTicketByTicketIdQuery({ ticketId: ticketId });

  const [addMessage, { isLoading: addMessageIsLoading }] =
    useAddMessageToTicketMutation();

  const [updateTicket, { isLoading: uploadIsLoading }] =
    useUpdateSingleTicketMutation();

  const userData = useSelector(selectAuthData);
  const role = userData?.userData?.role;
  const userId = userData?.userData?.id;
  const currentLanguage = useSelector(selectLanguage);

  const currentTicket = useSelector(selectSingleTicket);

  // STATE
  const [calendarValue, setCalendarValue] = useState(
    data?.resolution_date || null
  );

  const [ticketStatus, setTicketStatus] = useState(data?.status || null);
  const [editingTicket, setEditingTicket] = useState(false);
  const [message, setMessage] = useState("");
  const [showMore, setShowMore] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [fileUploaded, setFileUploaded] = useState(null);
  const [fileSizeError, setFileSizeError] = useState(false);

  // EFFECTS

  useEffect(() => {
    dispatch(getCurrentTicketId(ticketId));
  }, [ticketId, dispatch]);

  useEffect(() => {
    refetchSingleTicket(ticketId);
  }, [ticketId, refetchSingleTicket, location.pathname]);

  useEffect(() => {
    if (data?.status) {
      setTicketStatus(data.status);
    }
  }, [data?.status]);

  useEffect(() => {
    if (data?.resolution_date) {
      setCalendarValue(moment(data.resolution_date));
    }
  }, [data?.resolution_date]);

  // FUNCTIONS

  const handleDateChange = async (newValue) => {
    let formDataUpdate;

    if (!newValue) {
      setCalendarValue(null);
      formDataUpdate = new FormData();
      formDataUpdate.append("resolution_date", "");
    } else {
      const momentValue = moment(newValue);

      setCalendarValue(momentValue);

      formDataUpdate = new FormData();

      if (momentValue?.isValid()) {
        formDataUpdate.append(
          "resolution_date",
          momentValue.format("YYYY-MM-DD")
        );
      } else {
        console.error("Invalid date provided");
        return;
      }
    }

    try {
      await updateTicket({
        ticketId: ticketId,
        userId: currentTicket?.user_id,
        formData: formDataUpdate,
      }).unwrap();
    } catch (error) {
      console.error(`Failed to update ticket ${userId}`, error);
    }
  };
  const handleTicketStatusChange = (event) => {
    event.preventDefault();
    setTicketStatus(event.target.value);
    setEditingTicket(true);
  };

  const handleToggleShowDescription = () => {
    setShowMore(!showMore);
  };

  const handleSave = async (id) => {
    const objectToSave = new FormData();

    if (ticketStatus) {
      objectToSave.append("ticket_status", ticketStatus);
    }

    try {
      const result = await updateTicket({
        ticketId: id,
        userId: userId,
        formData: objectToSave,
      }).unwrap();
      if (result) {
        setEditingTicket(false);
        dispatch(getCurrentTicketId(ticketId));
      }
    } catch (error) {
      console.error("Failed to update ticket:", error);
    }
  };

  const handleCancel = () => {
    setEditingTicket(false);
    setTicketStatus(data?.status || null);
  };

  const handleGoBackToTickets = () => {
    navigate(adminDashboardRoute.tickets);
  };

  const handleNewMessageChange = (e) => {
    setMessage(e.target.value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file.size > process.env.REACT_APP_MAX_FILE_SIZE) {
      setFileSizeError(true);
    }

    if (file) {
      setFileUploaded(file);
      e.target.value = "";
      setFileSizeError(false);
    }
  };

  const handleOpenFileInput = () => {
    fileInput.current.click();
  };

  const handleSendMessage = async () => {
    const now = moment().format("YYYY-MM-DD");
    if (message || fileUploaded) {
      const messageData = {
        role: "admin",
        message: message ? message : "",
        created_at: now,
        file: fileUploaded ? fileUploaded : null,
      };

      const formData = new FormData();
      formData.append("message", messageData.message);
      formData.append("role", messageData.role);
      formData.append("created_at", messageData.created_at);
      if (messageData.file) {
        formData.append("file", messageData.file);
      }

      try {
        const result = await addMessage({
          ticketId: ticketId,
          userId: currentTicket?.user_id,
          messageData: formData,
        }).unwrap();
        if (result) {
          dispatch(getCurrentTicketId(ticketId));
          refetchSingleTicket();
          setMessage("");
          setFileUploaded(null);
        }
      } catch (error) {
        console.error("Failed to send message:", error);
      }
    }
  };

  // UTILS
  const StyledTypographyDescription = styled(Typography)({
    width: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "16px",
    display: "-webkit-box",
    WebkitLineClamp: showMore ? "unset" : 2,
    WebkitBoxOrient: "vertical",
    lineHeight: "1.5rem",
    maxHeight: showMore ? "none" : "fit-content",
    color: "var(--text-secondary)",
  });

  return (
    <ColumnContainer className="single-ticket-page" halfGap>
      {isLoading ? (
        <SkeletonLoading />
      ) : isError ? (
        <ErrorLoading />
      ) : data ? (
        <ColumnContainer>
          <RowContainer align="center">
            <TitleContainer>
              <ColumnContainer noGap>
                <RowContainer start align="center">
                  <RowContainer minGap start align="center">
                    <IconButton>
                      <ArrowBackIosRounded
                        onClick={() => handleGoBackToTickets()}
                      />
                    </IconButton>
                    <Typography variant="h3" component="h3">
                      Ticket #{ticketId}
                    </Typography>
                  </RowContainer>
                </RowContainer>
              </ColumnContainer>
            </TitleContainer>
            <RowContainer>
              <ColumnContainer minGap>
                <SelectComponent
                  width="200px"
                  value={ticketStatus || ""}
                  onChange={(event) => handleTicketStatusChange(event)}
                >
                  <MenuItem value="open">
                    {" "}
                    <FormattedMessage id="status.open" />
                  </MenuItem>
                  <MenuItem value="completed">
                    {" "}
                    <FormattedMessage id="status.completed" />
                  </MenuItem>
                  <MenuItem value="in_progress">
                    {" "}
                    <FormattedMessage id="status.inProgress" />
                  </MenuItem>
                  <MenuItem value="closed">
                    {" "}
                    <FormattedMessage id="status.closed" />
                  </MenuItem>
                </SelectComponent>
              </ColumnContainer>

              {editingTicket ? (
                <RowContainer start halfGap>
                  <DashboardButton
                    type="filled"
                    onClick={() => handleSave(ticketId)}
                  >
                    <FormattedMessage id="button.save" />
                  </DashboardButton>

                  <DashboardButton
                    type="outlined"
                    onClick={() => handleCancel()}
                  >
                    <FormattedMessage id="button.cancel" />
                  </DashboardButton>
                </RowContainer>
              ) : null}
            </RowContainer>
          </RowContainer>

          <ColumnContainer halfGap fullHeight>
            <ColumnContainer>
              <ColumnContainer
                noGap
                className={`resume-ticket-section ${role}`}
              >
                <Grid container spacing={2} overflow={"auto"}>
                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={6}
                    height={"fit-content"}
                  >
                    <ColumnContainer minGap>
                      <SubtitleContainer>
                        <Typography variant="p" component="p">
                          {data?.title}
                        </Typography>
                      </SubtitleContainer>
                      <RowContainer align="end" noGap>
                        <StyledTypographyDescription>
                          {data?.description}
                        </StyledTypographyDescription>
                        {data?.description ? (
                          <Typography
                            variant="p"
                            component="p"
                            fontSize="0.8rem"
                            color={"var(--secondary-color)"}
                            onClick={handleToggleShowDescription}
                          >
                            {showMore ? (
                              <FormattedMessage id="button.hide" />
                            ) : (
                              <FormattedMessage id="button.show" />
                            )}
                          </Typography>
                        ) : null}
                      </RowContainer>
                    </ColumnContainer>
                  </Grid>
                  <Grid
                    height={"100%"}
                    item
                    xs={0}
                    sm={0}
                    md={0}
                    lg={1}
                    sx={{
                      display: { xs: "none", lg: "flex" },
                      justifyContent: "center",
                    }}
                    paddingLeft="0px"
                  >
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{
                        borderColor: "var(--main-color)",
                      }}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={3}
                    height="fit-content"
                  >
                    <ColumnContainer
                      style={{ gap: "12px" }}
                      className="ticket-info"
                    >
                      <RowContainer start halfGap align="center">
                        <Typography
                          variant="p"
                          component="p"
                          className="title-info"
                        >
                          <FormattedMessage id="ticket.category" />:
                        </Typography>

                        <Chip
                          color={"warning"}
                          label={translateFilterKeys(
                            data?.category?.name,
                            currentLanguage
                          )}
                        />
                      </RowContainer>
                      <RowContainer start halfGap>
                        <Typography
                          variant="p"
                          component="p"
                          className="title-info"
                        >
                          <FormattedMessage id="ticket.created" />:
                        </Typography>
                        <Typography variant="p" component="p">
                          <FormatDateTime value={data?.created_at} />
                        </Typography>
                      </RowContainer>
                      <RowContainer start halfGap>
                        <Typography
                          variant="p"
                          component="p"
                          className="title-info"
                        >
                          <FormattedMessage id="ticket.sent" />:
                        </Typography>
                        <Link
                          to={`${adminDashboardRoute.users}/${data?.userId}`}
                        >
                          <Typography variant="p" component="p">
                            {data?.company_name}
                          </Typography>
                        </Link>
                      </RowContainer>
                      <RowContainer start halfGap>
                        <Typography
                          variant="p"
                          component="p"
                          className="title-info"
                        >
                          <FormattedMessage id="ticket.attachment" />:
                        </Typography>
                        <RowContainer start halfGap>
                          {data?.attachment?.file_name ? (
                            <Tooltip
                              title={
                                <FormattedMessage id="tooltip.downloadFile" />
                              }
                            >
                              <Typography
                                onClick={() =>
                                  handleDownloadClick(
                                    true,
                                    data.user_id,
                                    data?.attachment?.id
                                  )
                                }
                                variant="p"
                                component="p"
                                className="user-attachment"
                              >
                                {data?.attachment?.file_name}
                              </Typography>
                            </Tooltip>
                          ) : (
                            <Typography
                              variant="p"
                              component="p"
                              className="user-attachment"
                            >
                              -
                            </Typography>
                          )}
                        </RowContainer>
                      </RowContainer>
                      <RowContainer
                        align="center"
                        start
                        className="resolution-date"
                      >
                        <DatePicker
                          name="startDate"
                          className="date-picker"
                          value={moment(calendarValue)}
                          disablePast={true}
                          label={
                            <FormattedMessage id="ticket.resolutionDate" />
                          }
                          onChange={(newValue) => handleDateChange(newValue)}
                          renderInput={(params) => (
                            <TextField {...params} placeholder="gg.mm.aaaa" />
                          )}
                          slotProps={{
                            field: {
                              clearable: true,
                              placeholder: "Seleziona una data",
                              onClear: () => handleDateChange(null),
                              readOnly: true,
                            },
                          }}
                        />
                      </RowContainer>
                    </ColumnContainer>
                  </Grid>
                </Grid>
              </ColumnContainer>

              <ColumnContainer className="communication-section">
                <Typography variant="h6" component="h6">
                  <FormattedMessage id="ticket.communications" />
                </Typography>
                <ColumnContainer
                  className="chat-messages"
                  start
                  style={{
                    height:
                      currentTicket?.messages?.length === 0
                        ? "10vh"
                        : "fit-content",
                    overflowY: "scroll",
                    overflowX: "hidden",
                  }}
                >
                  {currentTicket?.messages?.length === 0 ? (
                    <Typography variant="p" component="p">
                      <FormattedMessage id="ticket.noMessages" />:
                    </Typography>
                  ) : (
                    currentTicket?.messages?.map((msg) => (
                      <RowContainer
                        style={{
                          justifyContent:
                            msg.hasOwnProperty("role") && msg.role === "user"
                              ? "flex-start"
                              : "flex-end",
                        }}
                      >
                        <ChatComponent element={msg} generalData={data} />
                      </RowContainer>
                    ))
                  )}
                </ColumnContainer>
                <RowContainer start>
                  {!openChat && data?.status !== "closed" ? (
                    <DashboardButton
                      type="filled"
                      onClick={() => setOpenChat(true)}
                    >
                      {data?.messages?.length === 0 ? (
                        <FormattedMessage id="button.startChat" />
                      ) : (
                        <FormattedMessage id="button.reply" />
                      )}
                    </DashboardButton>
                  ) : null}
                </RowContainer>
              </ColumnContainer>
              {openChat ? (
                <ColumnContainer style={{ gap: "8px" }}>
                  <RowContainer
                    align="end"
                    className="chat-text-field"
                    // style={{ position: "fixed" }}
                  >
                    <TextField
                      multiline
                      rows={4}
                      size="medium"
                      fullWidth
                      value={message || ""}
                      InputProps={{
                        inputProps: {
                          sx: {
                            "&::placeholder": {
                              color: "var(--text-primary)",
                              opacity: 1,
                            },
                          },
                        },

                        endAdornment: (
                          <RowContainer align="center" halfGap>
                            <Tooltip
                              title={
                                <FormattedMessage id="tooltip.uploadFile" />
                              }
                            >
                              <div>
                                <IconButton
                                  className="attach-btn"
                                  onClick={handleOpenFileInput}
                                >
                                  <AttachFileRounded />
                                </IconButton>
                              </div>
                            </Tooltip>
                            <input
                              accept={".zip, .mod, .bin"}
                              style={{ display: "none" }}
                              type="file"
                              ref={fileInput}
                              id="file-upload"
                              onChange={handleFileChange}
                            />
                            <DashboardButton
                              type="filled"
                              onClick={handleSendMessage}
                              disabled={
                                (!message && !fileUploaded) ||
                                addMessageIsLoading
                              }
                            >
                              <RowContainer align="center">
                                <FormattedMessage id="button.send" />
                                <SendRoundedIcon
                                  style={{
                                    transform: "rotate(-30deg)",
                                    marginBottom: "12px",
                                  }}
                                />
                              </RowContainer>
                            </DashboardButton>
                          </RowContainer>
                        ),
                      }}
                      onChange={handleNewMessageChange}
                      placeholder={intl.formatMessage({
                        id: "ticket.type",
                      })}
                    />
                  </RowContainer>
                  {fileUploaded ? (
                    <Chip
                      className="file-chip"
                      color="default"
                      label={
                        <RowContainer start minGap align="center">
                          <AttachFileRounded fontSize="small" />
                          {fileUploaded.name}
                        </RowContainer>
                      }
                      onDelete={() => setFileUploaded(null)}
                    />
                  ) : fileSizeError ? (
                    <Typography
                      component="p"
                      fontSize={"16px"}
                      color={"var(--error)"}
                      textAlign={"center"}
                    >
                      {<FormattedMessage id="file.size.error" />}
                    </Typography>
                  ) : null}
                </ColumnContainer>
              ) : null}
            </ColumnContainer>
          </ColumnContainer>
        </ColumnContainer>
      ) : null}
    </ColumnContainer>
  );
};
